/* ========== DRAWING ANCHOR EXTRACTOR ==========
 * A chart's position on its sheet lives inside a drawing-anchor element
 * (twoCellAnchor, oneCellAnchor, absoluteAnchor). This module pulls the
 * graphic-frame out of an anchor, identifies whether it points to a
 * standard (chart) or extended (chartEx) chart, and returns the rId +
 * range coordinates.
 * =============================================== */

import { qLocal, qLocalAll, qDirectChild } from "../xmlUtils";
import { CHART_EX_URI } from "./constants";
import { REL_NS } from "./namespaces";

/** Parse a <c:from>/<c:to> position node into integer cell offsets. */
export function readPosNode(node) {
  if (!node) return null;
  return {
    col:    parseInt(qLocal(node, "col")?.textContent    || "0", 10),
    row:    parseInt(qLocal(node, "row")?.textContent    || "0", 10),
    colOff: parseInt(qLocal(node, "colOff")?.textContent || "0", 10),
    rowOff: parseInt(qLocal(node, "rowOff")?.textContent || "0", 10),
  };
}

/** Parse an <xdr:ext cx="..." cy="..."/> extent (EMU). */
export function readExtNode(node) {
  if (!node) return null;
  const cx = parseInt(node.getAttribute("cx") || "0", 10);
  const cy = parseInt(node.getAttribute("cy") || "0", 10);
  if (!cx && !cy) return null;
  return { cx, cy };
}

/**
 * Pull the chart reference (rId) and "is this an extended chart?" flag out
 * of a graphicFrame. Returns null if the frame doesn't point to a chart.
 */
export function findChartRefInGraphicFrame(graphicFrame) {
  if (!graphicFrame) return null;
  const graphicData = qLocal(graphicFrame, "graphicData");
  if (!graphicData) return null;
  const uri = (graphicData.getAttribute("uri") || "").trim();

  const chartNode = qLocal(graphicData, "chart");
  if (!chartNode) return null;
  const rId = chartNode.getAttributeNS(REL_NS, "id") || chartNode.getAttribute("r:id");
  if (!rId) return null;

  const isExtended = uri === CHART_EX_URI
    || chartNode.namespaceURI === CHART_EX_URI
    || uri.indexOf("chartex") !== -1;
  return { rId, isExtended };
}

/**
 * Extract the chart reference plus positioning data from a drawing anchor.
 * Handles fallback into AlternateContent > Choice (Office sometimes wraps
 * modern charts there for back-compat).
 */
export function extractAnchorChart(anchor) {
  let graphicFrame = qDirectChild(anchor, "graphicFrame") || qLocal(anchor, "graphicFrame");

  if (!graphicFrame || !findChartRefInGraphicFrame(graphicFrame)) {
    const altContents = qLocalAll(anchor, "AlternateContent");
    for (const alt of altContents) {
      const choice = qLocal(alt, "Choice");
      if (choice) {
        const gf = qLocal(choice, "graphicFrame");
        if (gf && findChartRefInGraphicFrame(gf)) { graphicFrame = gf; break; }
      }
    }
  }

  const ref = findChartRefInGraphicFrame(graphicFrame);
  if (!ref) return null;

  const fromNode = qLocal(anchor, "from");
  const toNode   = qLocal(anchor, "to");
  const extNode  = qDirectChild(anchor, "ext") || qLocal(anchor, "ext");

  const tl  = readPosNode(fromNode) || { col: 0, row: 0, colOff: 0, rowOff: 0 };
  const br  = toNode ? readPosNode(toNode) : null;
  const ext = readExtNode(extNode);

  return { ref, range: { tl, br, ext } };
}
